import { find, findKey, flatten, omit, some } from "lodash";
import { useCallback, useMemo } from "react";

import { AiRequest } from "@/.gql/graphql";
import { useAiRequestsContext } from "@/providers";

/**
 * Хук для работы с информацией о AI запросах
 *
 * @description
 * Предоставляет функции для:
 * - Поиска категории AI запроса по ID
 * - Получения объекта AI запроса по ID
 * - Проверки возможности анимации для выбранных запросов
 * - Вычисления количества элементов в коллекции по элементу или ID
 *
 * @returns {Object} Объект с утилитами для работы с AI запросами
 * @property {Function} findCategoryById - Находит категорию AI запроса по ID
 * @property {Function} getAiRequestById - Получает объект AI запроса по ID
 * @property {Function} getCollectionCountByItem - Вычисляет количество элементов в коллекции по элементу или ID
 * @property {boolean} checkCanAnimate - Флаг возможности анимации
 *
 * @example
 * ```tsx
 * const { findCategoryById, getAiRequestById, getCollectionCountByItem, checkCanAnimate } = useAiRequestInfo();
 *
 * // Получить категорию
 * const category = findCategoryById('some-id');
 *
 * // Получить запрос
 * const request = getAiRequestById('some-id');
 *
 * // Проверить возможность анимации
 * if (checkCanAnimate) {
 *   // показать кнопку анимации
 * }
 *
 * // Получить количество элементов в коллекции по ID
 * const countById = getCollectionCountByItem('some-id');
 *
 * // Получить количество элементов в коллекции по объекту AiRequest
 * const countByItem = getCollectionCountByItem(someAiRequest);
 * ```
 */
export const useAiRequestInfo = () => {
  const aiRequests = useAiRequestsContext();

  /**
   * Находит категорию AI запроса по его ID
   *
   * @param {string} id - ID искомого AI запроса
   * @returns {string | null} Название категории или null, если не найдено
   */
  const findCategoryById = useCallback(
    (id: string): string | null => {
      if (!aiRequests || typeof aiRequests !== "object") {
        return null;
      }

      // Используем `findKey` для поиска ключа (категории), где есть тег с нужным `id`
      const category = findKey(aiRequests, (tagArray, key) => {
        if (key === "__typename" || !Array.isArray(tagArray)) {
          return false;
        }
        return some(tagArray, (tag) => tag.id === id);
      });

      return category || null;
    },
    [aiRequests],
  );

  /**
   * Получает объект AI запроса по его ID
   *
   * @param {string} id - ID искомого запроса
   * @returns {AiRequest | undefined} Объект запроса или undefined
   */
  const getAiRequestById = useCallback(
    (id: string): AiRequest | undefined => {
      if (!aiRequests || typeof aiRequests !== "object") {
        return undefined;
      }

      // Используем отдельные функции lodash вместо цепочки
      const withoutTypename = omit(aiRequests, "__typename");
      const allArrays = Object.values(withoutTypename);
      const flatArray = flatten(allArrays);
      return find(flatArray, { id });
    },
    [aiRequests],
  );

  /**
   * Проверяет возможность анимации для массива ID запросов.
   * Сначала ищет среди поз, затем среди костюмов.
   *
   * @param {string[]} ids - Массив ID запросов для проверки
   * @returns {boolean} true если найден запрос с canAnimate
   *
   * @example
   * const canAnimate = checkCanAnimate(['pose-id', 'costume-id']);
   */
  const checkCanAnimate = useCallback(
    (ids: string[]): boolean => {
      if (!ids?.length) {
        return false;
      }

      // Сначала ищем среди поз
      const poseRequest = ids
        .map((id) => {
          const category = findCategoryById(id);
          const request = getAiRequestById(id);
          return { category, request };
        })
        .find(({ category }) => category === "poses")?.request;

      // Если нашли среди поз, проверяем `canAnimate`
      if (poseRequest) {
        return poseRequest.canAnimate;
      }

      // Если среди поз не нашли, ищем среди костюмов
      const costumeRequest = ids
        .map((id) => {
          const category = findCategoryById(id);
          const request = getAiRequestById(id);
          return { category, request };
        })
        .find(({ category }) => category === "costumes")?.request;

      // Если нашли среди костюмов, проверяем `canAnimate`
      if (costumeRequest) {
        return costumeRequest.canAnimate;
      }
      // Если не нашли ни среди поз, ни среди костюмов, возвращаем false
      return false;
    },
    [findCategoryById, getAiRequestById],
  );

  // Мемоизируем флаттенинг массива на уровне хука
  const flattenedRequests = useMemo(() => {
    if (!aiRequests || typeof aiRequests !== "object") {
      return [];
    }
    return flatten(Object.values(omit(aiRequests, "__typename")));
  }, [aiRequests]);

  /**
   * Вычисляет количество элементов в коллекции, к которой принадлежит переданный элемент или ID
   *
   * @param {AiRequest | string} input - Объект AiRequest или ID элемента
   * @returns {number} Количество элементов в коллекции
   *
   * @example
   * const count = getCollectionCountByItem('some-id'); // Вернет количество элементов в коллекции
   * const countByItem = getCollectionCountByItem(someAiRequest); // Вернет количество элементов в коллекции
   */
  const getCollectionCountByItem = useCallback(
    (input: AiRequest | string): number => {
      // Ранний возврат при отсутствии данных
      if (!aiRequests || typeof aiRequests !== "object") {
        return 0;
      }

      // Получаем collectionTag
      const collectionTag =
        typeof input === "string"
          ? getAiRequestById(input)?.collectionTag
          : input.collectionTag;

      // Если нет тега коллекции - возвращаем 0
      if (!collectionTag) {
        return 0;
      }

      // Используем reduce с мемоизированным массивом
      return flattenedRequests.reduce(
        (count, request) =>
          request.collectionTag === collectionTag ? count + 1 : count,
        0,
      );
    },
    [aiRequests, getAiRequestById, flattenedRequests],
  );

  return {
    findCategoryById,
    getAiRequestById,
    checkCanAnimate,
    getCollectionCountByItem,
  };
};
